/**
 * card-variant-selector.js
 *
 * Fixes for Shopify Dawn theme product cards:
 *  1. Stops dropdown clicks from navigating to product page (stopPropagation)
 *  2. Updates live price display (with compare-at-price strikethrough) on change
 *  3. Syncs selected variant ID into the Add-to-Cart form hidden input
 *  4. Updates all card anchor links to carry ?variant=ID for detail page pre-selection
 *  5. Updates Add-to-Cart button availability state
 *
 * Deploy: upload to Assets, add to theme.liquid before </body>:
 *   <script src="{{ 'card-variant-selector.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // ─── Format cents → display string (mirrors Shopify money filter) ────────────
  function formatMoney(cents, isoCode, showCode) {
    if (!cents || cents === 0) return '';
    const dollars = (cents / 100).toFixed(2);
    let symbol = '$';
    try {
      const parts = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: isoCode || 'USD',
        currencyDisplay: 'narrowSymbol'
      }).formatToParts(1);
      const sym = parts.find(function (p) { return p.type === 'currency'; });
      if (sym) symbol = sym.value;
    } catch (e) {}
    const formatted = symbol + dollars;
    return showCode && isoCode ? formatted + ' ' + isoCode : formatted;
  }

  // ─── Per-card initialiser ─────────────────────────────────────────────────────

  function initCard(selectorContainer) {
    const productId = selectorContainer.dataset.productId;
    const sectionId = selectorContainer.dataset.sectionId;
    const baseUrl   = selectorContainer.dataset.productUrl;
    const jsonId    = selectorContainer.dataset.variantsJson;
    const isoCode   = selectorContainer.dataset.currencyIso || 'USD';
    const showCode  = selectorContainer.dataset.currencyCodeEnabled === 'true';

    // ── Parse variant JSON ──
    const jsonEl = document.getElementById(jsonId);
    if (!jsonEl) return;
    let variants;
    try { variants = JSON.parse(jsonEl.textContent); }
    catch (e) { console.warn('[card-variant-selector] JSON parse error', jsonId, e); return; }

    // ── DOM refs ──
    const select       = selectorContainer.querySelector('select.card-variant-select');
    const hiddenId     = document.getElementById('card-variant-id-' + sectionId + '-' + productId);
    const formInput    = document.getElementById('card-form-variant-' + sectionId + '-' + productId);
    const priceWrapper = document.getElementById('card-price-' + sectionId + '-' + productId);
    const atcButton    = document.getElementById('quick-add-' + sectionId + productId + '-submit');
    const cardLinks    = document.querySelectorAll(
      '#CardLink-' + sectionId + '-' + productId + ', ' +
      '#StandardCardNoMediaLink-' + sectionId + '-' + productId
    );

    if (!select) return;

    // ── Stop clicks inside selector from bubbling to parent <a> ──────────────
    selectorContainer.addEventListener('click',      stopBubble, true);
    selectorContainer.addEventListener('mousedown',  stopBubble, true);
    selectorContainer.addEventListener('touchstart', stopBubble, { capture: true, passive: true });
    selectorContainer.addEventListener('touchend',   stopBubble, true);

    function stopBubble(e) { e.stopPropagation(); }

    // ── Handle variant change ─────────────────────────────────────────────────
    function onVariantChange() {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const variantId   = parseInt(selectedOption.value, 10);
      const price       = parseInt(selectedOption.dataset.price, 10)          || 0;
      const compareAt   = parseInt(selectedOption.dataset.compareAtPrice, 10) || 0;
      const isAvailable = selectedOption.dataset.available === 'true';

      // ── FIX: on_sale requires a real positive compare-at price above actual price.
      //    compareAt === 0 must NEVER set isOnSale = true, because Dawn CSS hides
      //    .price__regular and shows .price__sale when price--on-sale is present.
      //    If .price__sale has no strikethrough content the actual price becomes
      //    invisible. ──────────────────────────────────────────────────────────
      const isOnSale = compareAt > 0 && compareAt > price;

      // 1. Sync hidden variant ID inputs
      if (hiddenId)  hiddenId.value  = variantId;
      if (formInput) formInput.value = variantId;

      // 2. Update card links with ?variant= param
      const variantUrl = baseUrl + '?variant=' + variantId;
      cardLinks.forEach(function (link) { link.href = variantUrl; });

      // 3. Update price display ─────────────────────────────────────────────
      //    IMPORTANT: Do NOT replace priceWrapper.innerHTML.
      //    Dawn's CSS works by toggling the `price--on-sale` class on the
      //    outer .price div. Both .price__regular AND .price__sale divs must
      //    always exist in the DOM — the CSS shows one and hides the other.
      //    Replacing innerHTML with only one of them breaks that mechanism
      //    and causes the price to disappear on non-sale products.
      if (priceWrapper) {
        const priceDiv = priceWrapper.querySelector('.price');

        if (priceDiv) {
          // Toggle the sale/sold-out classes — CSS does the show/hide
          priceDiv.classList.toggle('price--on-sale',  isOnSale);
          priceDiv.classList.toggle('price--sold-out', !isAvailable);

          // Update the regular price span (visible when NOT on sale)
          const regularSpan = priceDiv.querySelector('.price__regular .price-item--regular');
          if (regularSpan) {
            regularSpan.textContent = formatMoney(price, isoCode, showCode);
          }

          // Update the sale price span (visible when on sale)
          const salePriceSpan = priceDiv.querySelector('.price__sale .price-item--sale');
          if (salePriceSpan) {
            salePriceSpan.textContent = formatMoney(price, isoCode, showCode);
          }

          // Update or show/hide the strikethrough compare-at price
          const strikeSpan = priceDiv.querySelector('.price__sale s.price-item--regular');
          if (isOnSale) {
            const compareFormatted = formatMoney(compareAt, isoCode, showCode);
            if (strikeSpan) {
              // Already exists — just update text and make sure it's visible
              strikeSpan.textContent  = compareFormatted;
              strikeSpan.style.display = '';
            } else {
              // Doesn't exist yet (first variant had no sale price) — create it
              const saleInnerSpan = priceDiv.querySelector('.price__sale > span');
              if (saleInnerSpan) {
                const s = document.createElement('s');
                s.className   = 'price-item price-item--regular';
                s.textContent = compareFormatted;
                saleInnerSpan.appendChild(s);
              }
            }
          } else {
            // Not on sale — hide the strikethrough if it exists
            if (strikeSpan) strikeSpan.style.display = 'none';
          }
        }
      }

      // 4. Update ATC button state ───────────────────────────────────────────
      if (atcButton) {
        const btnText     = atcButton.querySelector('span:not(.sold-out-message):not(.icon-wrap):not(.loading-overlay__spinner)');
        const soldOutSpan = atcButton.querySelector('.sold-out-message');

        if (isAvailable) {
          atcButton.removeAttribute('disabled');
          if (btnText)     btnText.textContent = 'Add to cart';
          if (soldOutSpan) soldOutSpan.classList.add('hidden');
        } else {
          atcButton.setAttribute('disabled', 'disabled');
          if (btnText)     btnText.textContent = 'Sold out';
          if (soldOutSpan) soldOutSpan.classList.remove('hidden');
        }
      }
    }

    select.addEventListener('change', onVariantChange);

    // Run immediately on load to sync everything with the default selected option
    onVariantChange();
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('.card-variant-selectors').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();